/**
 * API des PARTENARIATS entre entreprises.
 *
 * Un partenariat lie l'entreprise d'un éleveur (le « client ») à celle d'un installateur ou
 * d'un groupement (le « prestataire »). Il naît d'une DEMANDE, que l'autre entreprise doit
 * accepter ou refuser — personne ne se lie unilatéralement.
 *
 * Le front n'envoie qu'un EMAIL : c'est le backend qui résout la cible, décide qui est client
 * et qui est prestataire, et déduit la nature du lien. Aucun identifiant d'entreprise ne
 * transite d'ici, il n'y a donc rien à falsifier.
 */
import { BreedingPartner, breedingPartners } from "../utils/breedingPartners";
import { apiFetchJson, apiFetchRoute } from "./apiFetchRoute";

/** Entreprise d'en face, telle que renvoyée par le backend (nom résolu côté DATA via USERS). */
export type PartnershipCompany = {
  id: number;
  company_name: string;
};

/**
 * Un partenariat prêt à afficher.
 *
 * `role` est la position de MON entreprise dans ce lien :
 *  - "client"   → je reçois la prestation, `company` est mon installateur/groupement ;
 *  - "provider" → je fournis la prestation, `company` est mon client éleveur.
 *
 * ⚠️ `company` et `partnership_type` sont VIDES quand il s'agit d'une demande que J'AI émise
 * et qui est encore en attente : le backend les masque pour ne pas confirmer que l'email
 * saisi correspond à un compte existant (même raison que la réponse opaque du POST). Toujours
 * tester `company?.company_name` avant de l'afficher.
 *
 * `can_respond` dit s'il faut proposer Accepter/Refuser : le backend le met à vrai uniquement
 * si la demande est en attente ET que je ne suis pas celui qui l'a émise. Ne pas le recalculer
 * ici — c'est le serveur qui fait foi.
 *
 * `response_date` n'est aujourd'hui JAMAIS renvoyé (limite connue du générateur Go côté
 * backend, cf. skill copernic-data) : ne pas s'appuyer dessus pour l'affichage.
 */
export type PartnershipView = {
  id: number;
  /** "" quand le backend masque la cible (demande émise par moi, encore en attente). */
  partnership_type: "installer" | "group" | "";
  status: "pending" | "accepted" | "refused";
  role: "client" | "provider";
  /** Champs à 0/"" quand le backend masque la cible — voir le commentaire ci-dessus. */
  company: PartnershipCompany;
  creation_date: string;
  response_date?: string;
  can_respond: boolean;
};

export type PartnershipStatusFilter = "pending" | "accepted" | "refused";
export type PartnershipRoleFilter = "client" | "provider";

/**
 * Liste les partenariats de MON entreprise (le backend la déduit de la session : aucun
 * identifiant à passer). Filtres facultatifs par état et par sens du lien.
 *
 * Un 204 (aucun partenariat) est un cas normal et renvoie une liste vide, pas une erreur.
 */
export const fetchPartnerships = async (filters?: {
  status?: PartnershipStatusFilter;
  role?: PartnershipRoleFilter;
  /**
   * Ne garder que les partenariats où l'appelant peut réellement associer un produit.
   * Sans effet pour un installateur (il le peut chez tous ses clients) ; pour un
   * COLLABORATEUR, restreint à ceux où son chef lui a accordé le droit — sinon le
   * sélecteur lui proposerait des clients qui le refuseraient ensuite en 403.
   */
  canAssociate?: boolean;
}): Promise<{ partnerships: PartnershipView[]; error: string | null }> => {
  try {
    const data = (await apiFetchJson("getPartnerships", undefined, {
      query: {
        status: filters?.status,
        role: filters?.role,
        can_associate: filters?.canAssociate ? "true" : undefined,
      },
    })) as PartnershipView[] | undefined;

    // 204 -> undefined ; une slice Go nil peut aussi arriver en `null`.
    return { partnerships: data ?? [], error: null };
  } catch (err) {
    console.error("Erreur lors du chargement des partenariats:", err);
    return {
      partnerships: [],
      error: "Impossible de charger les partenariats",
    };
  }
};

/**
 * Demande un partenariat à partir d'un email.
 *
 * ⚠️ Le backend répond TOUJOURS 202 avec le même message, que l'email existe ou non, que la
 * personne soit du bon type ou non, qu'une demande soit déjà en cours ou non. C'est
 * délibéré : sans ça, on pourrait énumérer des emails et découvrir qui est client de Copernic
 * et avec quel rôle.
 *
 * Le front DOIT afficher ce message tel quel et ne jamais tenter de deviner ce qui s'est
 * réellement passé — pas de « email introuvable », pas de « déjà partenaire ».
 */
export const requestPartnership = async (
  email: string
): Promise<{
  success: boolean;
  message: string | null;
  error: string | null;
}> => {
  try {
    // La route est déclarée `request: "x"` dans les TSV (corps composé d'un seul champ,
    // pas de struct SQL derrière) : `RequestTypeOf` vaut donc `undefined` et le cast est
    // nécessaire pour envoyer le corps. Le contrat réel est `{ email }`.
    const response = await apiFetchRoute(
      "postPartnerships",
      { email } as never,
      { headers: { "Content-type": "application/json" } }
    );

    if (!response.ok) {
      // 403 : type de compte non autorisé à créer un partenariat (assistant, admin…).
      if (response.status === 403) {
        return {
          success: false,
          message: null,
          error: "Votre type de compte ne permet pas de créer un partenariat.",
        };
      }
      return {
        success: false,
        message: null,
        error: "Impossible d'envoyer la demande",
      };
    }

    const data = (await response.json()) as { message?: string };
    return { success: true, message: data?.message ?? null, error: null };
  } catch (err) {
    console.error("Erreur lors de la demande de partenariat:", err);
    return {
      success: false,
      message: null,
      error: "Impossible d'envoyer la demande",
    };
  }
};

/** Accepte une demande en attente. Réservé côté backend à l'entreprise destinataire. */
export const acceptPartnership = async (
  id: number
): Promise<{ success: boolean; error: string | null }> =>
  respondToPartnership("putPartnershipsIdAccept", id);

/** Refuse une demande en attente. */
export const refusePartnership = async (
  id: number
): Promise<{ success: boolean; error: string | null }> =>
  respondToPartnership("putPartnershipsIdRefuse", id);

const respondToPartnership = async (
  route: "putPartnershipsIdAccept" | "putPartnershipsIdRefuse",
  id: number
): Promise<{ success: boolean; error: string | null }> => {
  try {
    const response = await apiFetchRoute(route, undefined, { params: { id } });

    if (!response.ok) {
      // 403 : je ne suis pas du côté destinataire (ou j'ai émis la demande moi-même).
      // 404 : demande inexistante ou DÉJÀ traitée — le plus probable si deux onglets
      //       sont ouverts, d'où un message qui invite à recharger plutôt qu'une erreur sèche.
      if (response.status === 404) {
        return {
          success: false,
          error: "Cette demande a déjà été traitée. Rafraîchissez la page.",
        };
      }
      if (response.status === 403) {
        return {
          success: false,
          error: "Vous n'êtes pas autorisé à répondre à cette demande.",
        };
      }
      return { success: false, error: "Une erreur est survenue" };
    }

    return { success: true, error: null };
  } catch (err) {
    console.error("Erreur lors de la réponse à la demande:", err);
    return { success: false, error: "Une erreur est survenue" };
  }
};

/** Rompt un partenariat. Réservé côté backend aux deux entreprises concernées. */
export const deletePartnership = async (
  id: number
): Promise<{ success: boolean; error: string | null }> => {
  try {
    const response = await apiFetchRoute("deletePartnershipsId", undefined, {
      params: { id },
    });

    if (!response.ok) {
      return { success: false, error: "Impossible de rompre ce partenariat" };
    }

    return { success: true, error: null };
  } catch (err) {
    console.error("Erreur lors de la rupture du partenariat:", err);
    return { success: false, error: "Impossible de rompre ce partenariat" };
  }
};

/**
 * PARTAGE DE RESSOURCES avec un partenaire.
 *
 * L'éleveur (le « client » du partenariat) choisit ce qu'il expose à son prestataire.
 * Le partage est enregistré au niveau ENTREPRISE : tous les installateurs de l'entreprise
 * partenaire y accèdent, y compris ceux qui arriveront plus tard. Rien n'est recopié en
 * liens par compte — rompre le partenariat retire tout automatiquement.
 *
 * Seuls les produits (`device`) sont partageables aujourd'hui ; bâtiments et élevages
 * suivront (le backend refuse les autres types pour l'instant).
 */

/** Un produit de mon entreprise, avec l'état de son partage sur CE partenariat. */
export type ShareableDevice = {
  id: number;
  uuid: string;
  name: string;
  type: string;
  shared: boolean;
};

export type ShareableBuilding = {
  id: number;
  name: string;
  shared: boolean;
  devices: ShareableDevice[];
};

export type ShareableBreeding = {
  id: number;
  name: string;
  shared: boolean;
  buildings: ShareableBuilding[];
};

/**
 * Ce que le client peut partager, en arborescence.
 *
 * Trois niveaux, chacun avec son propre `shared` : cocher un ÉLEVAGE donne accès à tous
 * ses bâtiments et produits — y compris ceux ajoutés PLUS TARD, la résolution se faisant
 * à la lecture côté backend. Cocher un bâtiment couvre ses produits. Cocher un produit
 * ne couvre que lui.
 *
 * `orphan_devices` : les produits rattachés à aucun bâtiment. Sans eux, un produit associé
 * à l'entreprise mais pas encore placé serait impartageable.
 */
export type ShareableTree = {
  breedings: ShareableBreeding[];
  orphan_devices: ShareableDevice[];
};

/** Type de ressource partageable — doit correspondre à EnumShareResourceType côté backend. */
export type ShareResourceType = "device" | "building" | "breeding";

/**
 * Liste MES produits et indique lesquels sont déjà partagés avec ce partenaire.
 *
 * Le backend renvoie la liste complète (pas seulement les partages) : l'écran affiche
 * toutes les cases, cochées ou non, sans second appel.
 *
 * 204 = l'entreprise n'a aucun produit (cas normal, liste vide).
 */
export const fetchShareableTree = async (
  partnershipId: number
): Promise<{ tree: ShareableTree; error: string | null }> => {
  const empty: ShareableTree = { breedings: [], orphan_devices: [] };
  try {
    const data = (await apiFetchJson("getPartnershipsIdShares", undefined, {
      params: { id: partnershipId },
    })) as ShareableTree | undefined;

    return { tree: data ?? empty, error: null };
  } catch (err) {
    console.error("Erreur lors du chargement des ressources partageables:", err);
    return { tree: empty, error: "Impossible de charger vos ressources" };
  }
};

/** Partage une ressource (produit, bâtiment ou élevage). Idempotent côté backend. */
export const shareResource = async (
  partnershipId: number,
  resourceType: ShareResourceType,
  idResource: number
): Promise<{ success: boolean; error: string | null }> => {
  try {
    // Route déclarée `request: "x"` (corps sans struct SQL derrière) : le cast est
    // nécessaire pour envoyer le corps. Contrat réel : { resource_type, id_resource }.
    const response = await apiFetchRoute(
      "postPartnershipsIdShares",
      { resource_type: resourceType, id_resource: idResource } as never,
      { params: { id: partnershipId } }
    );

    if (!response.ok) {
      return { success: false, error: "Impossible de partager ce produit" };
    }
    return { success: true, error: null };
  } catch (err) {
    console.error("Erreur lors du partage du produit:", err);
    return { success: false, error: "Impossible de partager ce produit" };
  }
};

/** Retire une ressource du partage. Idempotent côté backend. */
export const unshareResource = async (
  partnershipId: number,
  resourceType: ShareResourceType,
  idResource: number
): Promise<{ success: boolean; error: string | null }> => {
  try {
    const response = await apiFetchRoute(
      "deletePartnershipsIdSharesResource_typeId_resource",
      undefined,
      {
        params: {
          id: partnershipId,
          resource_type: resourceType,
          id_resource: idResource,
        },
      }
    );

    if (!response.ok) {
      return { success: false, error: "Impossible de retirer ce partage" };
    }
    return { success: true, error: null };
  } catch (err) {
    console.error("Erreur lors du retrait du partage:", err);
    return { success: false, error: "Impossible de retirer ce partage" };
  }
};

/**
 * AFFECTATION DE COLLABORATEURS sur un partage.
 *
 * Miroir du partage : le client choisit ce qu'il expose, le prestataire choisit qui dans
 * son équipe s'en occupe.
 *
 * Règle d'accès qui en découle, appliquée par le backend :
 *  - installer / group (types principaux) : voient TOUT ce que leurs clients partagent ;
 *  - installer_assistant / group_assistant : ne voient QUE les partages où ils sont affectés.
 */

/** Un collaborateur de mon entreprise, avec son état d'affectation sur le partage consulté. */
export type ShareCollaborator = {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  assigned: boolean;
};

/**
 * Liste mes collaborateurs et indique lesquels sont affectés à ce partage.
 * 204 = l'entreprise n'a aucun collaborateur (cas normal, liste vide).
 */
export const fetchShareCollaborators = async (
  partnershipId: number,
  shareId: number
): Promise<{ collaborators: ShareCollaborator[]; error: string | null }> => {
  try {
    const data = (await apiFetchJson(
      "getPartnershipsIdSharesId_shareContacts",
      undefined,
      { params: { id: partnershipId, id_share: shareId } }
    )) as ShareCollaborator[] | undefined;

    return { collaborators: data ?? [], error: null };
  } catch (err) {
    console.error("Erreur lors du chargement des collaborateurs:", err);
    return { collaborators: [], error: "Impossible de charger vos collaborateurs" };
  }
};

/** Affecte un collaborateur à un partage. Idempotent côté backend. */
export const assignShareCollaborator = async (
  partnershipId: number,
  shareId: number,
  idAccount: number
): Promise<{ success: boolean; error: string | null }> => {
  try {
    // Route `request: "x"` : le cast permet d'envoyer le corps { id_account }.
    const response = await apiFetchRoute(
      "postPartnershipsIdSharesId_shareContacts",
      { id_account: idAccount } as never,
      { params: { id: partnershipId, id_share: shareId } }
    );

    if (!response.ok) {
      return { success: false, error: "Impossible d'affecter ce collaborateur" };
    }
    return { success: true, error: null };
  } catch (err) {
    console.error("Erreur lors de l'affectation:", err);
    return { success: false, error: "Impossible d'affecter ce collaborateur" };
  }
};

/** Retire un collaborateur d'un partage. Idempotent côté backend. */
export const unassignShareCollaborator = async (
  partnershipId: number,
  shareId: number,
  idAccount: number
): Promise<{ success: boolean; error: string | null }> => {
  try {
    const response = await apiFetchRoute(
      "deletePartnershipsIdSharesId_shareContactsId_account",
      undefined,
      {
        params: {
          id: partnershipId,
          id_share: shareId,
          id_account: idAccount,
        },
      }
    );

    if (!response.ok) {
      return { success: false, error: "Impossible de retirer ce collaborateur" };
    }
    return { success: true, error: null };
  } catch (err) {
    console.error("Erreur lors du retrait:", err);
    return { success: false, error: "Impossible de retirer ce collaborateur" };
  }
};

/**
 * Une ressource qu'un client m'a partagée, avec le nombre de collaborateurs affectés.
 *
 * Peut être un produit, un bâtiment ou un élevage : `label` porte le nom lisible et
 * `sublabel` la précision (uuid + type pour un produit, « Bâtiment » / « Élevage » sinon).
 * Le front n'a donc pas à connaître les trois formes.
 */
export type ReceivedShare = {
  id_share: number;
  resource_type: ShareResourceType;
  id_resource: number;
  label: string;
  sublabel: string;
  contacts_count: number;
  /**
   * Parenté, pour reconstruire l'arborescence côté écran. `0` quand le niveau ne
   * s'applique pas (un élevage n'a pas de parent) ou que la ressource n'est rattachée
   * à rien. Sans elle, un produit et l'élevage qui le contient s'afficheraient côte à
   * côte, sans qu'on voie que cocher l'élevage couvre déjà le produit.
   */
  id_breeding: number;
  id_building: number;
};

/**
 * Liste ce qu'un client m'a partagé sur ce partenariat.
 *
 * Pendant de `fetchShareableDevices`, vu de l'autre côté : c'est cette route qui fournit
 * les `id_share`, indispensables pour affecter des collaborateurs.
 */
export const fetchReceivedShares = async (
  partnershipId: number
): Promise<{ shares: ReceivedShare[]; error: string | null }> => {
  try {
    const data = (await apiFetchJson("getPartnershipsIdReceivedshares", undefined, {
      params: { id: partnershipId },
    })) as ReceivedShare[] | undefined;

    return { shares: data ?? [], error: null };
  } catch (err) {
    console.error("Erreur lors du chargement des ressources partagées:", err);
    return { shares: [], error: "Impossible de charger les ressources partagées" };
  }
};

/**
 * ÉQUIPE d'un partenariat : qui, chez le prestataire, accède aux ressources du client.
 *
 * Deux niveaux, volontairement distincts :
 *  - **accès complet** : le collaborateur voit tout ce que ce client partage, y compris
 *    les ressources partagées PLUS TARD. Même portée que son chef, sur ce client ;
 *  - **accès ciblé** : il ne voit que les ressources sur lesquelles il est affecté.
 *
 * Les deux coexistent : activer l'accès complet ne supprime pas les affectations ciblées,
 * il les rend seulement sans effet. Le désactiver rétablit l'état précédent.
 */
export type CollaboratorAccess = {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  avatar: string;
  full_access: boolean;
  /** Peut associer un produit à ce client (droit par partenariat, pas global). */
  can_associate: boolean;
  assigned_share_ids: number[];
};

/** Équipe et niveau d'accès de chacun, en un seul appel (pas un par collaborateur). */
export const fetchPartnershipCollaborators = async (
  partnershipId: number
): Promise<{ collaborators: CollaboratorAccess[]; error: string | null }> => {
  try {
    const data = (await apiFetchJson("getPartnershipsIdCollaborators", undefined, {
      params: { id: partnershipId },
    })) as CollaboratorAccess[] | undefined;

    return { collaborators: data ?? [], error: null };
  } catch (err) {
    console.error("Erreur lors du chargement de l'équipe:", err);
    return { collaborators: [], error: "Impossible de charger votre équipe" };
  }
};

/** Droits activables pour un collaborateur, sur UN partenariat. */
export type CollaboratorRight = "full_access" | "can_associate";

/**
 * Active ou désactive un droit d'un collaborateur.
 *
 * Les deux droits sont indépendants et vivent sur la même ligne côté backend : basculer
 * l'un n'affecte jamais l'autre.
 */
export const setCollaboratorRight = async (
  partnershipId: number,
  idAccount: number,
  right: CollaboratorRight,
  value: boolean
): Promise<{ success: boolean; error: string | null }> => {
  try {
    const response = await apiFetchRoute(
      "putPartnershipsIdCollaboratorsId_account",
      { right, value } as never,
      { params: { id: partnershipId, id_account: idAccount } }
    );
    if (!response.ok) {
      return { success: false, error: "Impossible de modifier ce droit" };
    }
    return { success: true, error: null };
  } catch (err) {
    console.error("Erreur lors de la modification du droit:", err);
    return { success: false, error: "Impossible de modifier ce droit" };
  }
};

/**
 * Partenaires (installateurs, groupements) ayant accès à tout ou partie d'un élevage.
 *
 * Il n'existe pas de route « partenaires d'un élevage » : on lit l'arbre de partage de
 * chaque partenariat accepté où je suis client. Un appel par partenariat, ce qui reste
 * raisonnable pour le nombre de partenaires d'un éleveur.
 */
export const fetchBreedingPartners = async (
  breedingId: number
): Promise<{ partners: BreedingPartner[]; error: string | null }> => {
  const { partnerships, error } = await fetchPartnerships({
    status: "accepted",
    role: "client",
  });
  if (error) return { partners: [], error };

  const results = await Promise.all(
    partnerships.map(async (partnership) => ({
      partnership,
      ...(await fetchShareableTree(partnership.id)),
    }))
  );
  const failed = results.find((result) => result.error);
  return {
    partners: breedingPartners(breedingId, results),
    error: failed ? failed.error : null,
  };
};
